import { Component, computed, input, linkedSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  createAngularTable,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  Table,
  ColumnFiltersState,
  FlexRenderDirective,
} from '@tanstack/angular-table';
import { IconComponent } from '@ui/components/icon';
import { SkeletonComponent } from '@shared/components/skeleton/skeleton.component';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    FlexRenderDirective,
    SkeletonComponent,
  ],
  templateUrl: './table.component.html',
})
export class TableComponent<TData> {
  data = input<TData[]>();
  columns = input<ColumnDef<TData>[]>();
  columnFilters = input<ColumnFiltersState>([]);
  loading = input<boolean>(false);
  columnClasses = input<Record<string, string>>({});

  _columnFilters = linkedSignal<ColumnFiltersState>(() => this.columnFilters());

  table: Table<TData> = createAngularTable(
    computed(() => ({
      data: this.data() ?? [],
      columns: this.columns() ?? [],
      state: {
        columnFilters: this._columnFilters(),
      },
      onColumnFiltersChange: (filters) => {
        if (typeof filters === 'function') {
          this._columnFilters.set(filters(this._columnFilters()));
        } else {
          this._columnFilters.set(filters);
        }
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      enableSorting: true,
    })),
  );

  getVisibleLeafHeaders() {
    const headerGroups = this.table.getHeaderGroups();
    if (!headerGroups || headerGroups.length === 0) {
      return [];
    }
    const lastGroup = headerGroups[headerGroups.length - 1];
    return lastGroup.headers.filter((h) => !h.isPlaceholder);
  }

  getColumnClass(columnId: string | undefined): string {
    if (!columnId) return '';
    const classes = this.columnClasses();
    return classes[columnId] ?? '';
  }
}
